import {
   Box,
   Flex,
   Heading,
   HStack,
   Link,
   LinkProps,
   Text,
   TextProps,
   useBreakpoint,
} from '@chakra-ui/react';
import { GuideProductsSection, GuideProductsData } from './Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/pro-regular-svg-icons/faArrowDown';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { MutableRefObject, useRef } from 'react';
import { _js } from '@ifixit/localize';
import { FaIcon } from '@ifixit/icons';
import { faLink } from '@fortawesome/pro-solid-svg-icons/faLink';

export interface ComponentData {
   productData: GuideProductsData;
   introData: GuideIntroData;
}
export interface GuideIntroData {
   introHtml: string;
}

export function GuideProducts(props: ComponentData) {
   const hasIntro = Boolean(props.introData.introHtml);

   const introRef = useRef<HTMLDivElement>();

   if (!hasIntro) {
      return (
         <Flex flexDir={{ base: 'column-reverse', sm: 'row' }}>
            {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
            <GuideItems hasIntro={hasIntro} {...props.productData} introRef={introRef}></GuideItems>
            <Flex flexDir={{ base: 'column-reverse', sm: 'column' }} flex={1}></Flex>
         </Flex>
      );
   }

   return (
      <Flex flexDir={{ base: 'column-reverse', sm: 'row' }}>
         {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
         <GuideIntro {...props.introData} introRef={introRef}></GuideIntro>
         {/* @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message */}
         <GuideItems hasIntro={hasIntro} {...props.productData} introRef={introRef}></GuideItems>
      </Flex>
   );
}

function GuideIntro({
   introHtml,
   introRef,
}: GuideIntroData & { introRef: MutableRefObject<HTMLDivElement> }) {
   if (!introHtml) {
      return null;
   }

   const breakpoint = useBreakpoint({ ssr: false });
   const isMobile = breakpoint == 'base';

   return (
      <Flex
         flexDir={{ base: 'column-reverse', sm: 'column' }}
         flex={1}
         alignSelf="flex-start"
         position="sticky"
         top={0}
      >
         <Flex
            flexDir="column"
            margin={0}
            paddingTop={4}
            paddingRight={{ base: 4, sm: 2 }}
            paddingBottom={4}
            paddingLeft={{ base: 4, sm: 0 }}
            className="introduction-container"
         >
            <HStack
               spacing={{ base: 0, sm: 3 }}
               marginBottom={{ base: '12px', sm: '16px' }}
               alignItems={{ base: 'flex-start', sm: 'baseline' }}
               flexDir={{ base: 'column-reverse', sm: 'row' }}
            >
               <Heading
                  fontSize={{ base: '20px', sm: '24px' }}
                  fontWeight="500"
                  color="gray.800"
                  display="flex"
                  margin="0"
               >
                  {_js('Introduction')}
               </Heading>
               <GoToFirstStep margin={0} paddingBottom={{ base: 3, sm: 0 }} />
            </HStack>
            <Box
               dangerouslySetInnerHTML={{ __html: introHtml }}
               ref={introRef}
               sx={{ '& > *::first-of-type': { padding: 0, margin: 0 } }}
            ></Box>
         </Flex>
      </Flex>
   );
}

function GoToFirstStep(props: LinkProps & TextProps) {
   const firstStep = document.getElementsByClassName('step-title')?.[0];

   if (!firstStep) {
      return null;
   }

   const scrollToFirstStep = () => {
      trackInPiwikAndGA({
         eventCategory: 'Guide Products',
         eventAction: 'Guide Products - Go to step 1',
      });
      firstStep?.scrollIntoView({ behavior: 'smooth' });
   };

   return (
      <Text
         as={Link}
         onClick={scrollToFirstStep}
         fontSize="lg"
         textColor="brand.500"
         noOfLines={1}
         {...props}
      >
         {_js('Go to step 1') + ' '}
         <FontAwesomeIcon fontSize="16px" icon={faArrowDown} />
      </Text>
   );
}

function GuideItems({
   hasIntro,
   tools,
   parts,
   kits,
   hasAffiliateLink,
   introRef,
}: GuideProductsData & { hasIntro: boolean; introRef: MutableRefObject<HTMLDivElement> }) {
   const hasProducts = [tools, parts, kits].reduce((sum, arr) => sum + arr.length, 0) > 0;
   if (!hasProducts) {
      return null;
   }

   return (
      <Flex
         flexDir="column"
         // @ts-expect-error TS(2322) FIXME: Type '{ base: null; sm: number; }' is not assignab... Remove this comment to see the full error message
         flex={{ base: null, sm: 1 }}
         paddingTop={4}
         paddingRight={{ base: 4, sm: 0 }}
         paddingBottom={4}
         paddingLeft={{ base: 4, sm: hasIntro ? 4 : 0 }}
      >
         {/* Translators: Heading for a list of parts and tools needed to complete a guide */}
         <Heading
            fontSize={{ base: '20px', sm: '24px' }}
            fontWeight="500"
            color="gray.800"
            marginTop="0px"
            marginBottom={{ base: '12px', sm: '16px' }}
         >
            {_js('What you need')}
         </Heading>
         <GuideProductsSection
            kits={kits}
            tools={tools}
            parts={parts}
            hasAffiliateLink={hasAffiliateLink}
            introRef={introRef}
         />
         {hasAffiliateLink && affiliateDisclosure()}
      </Flex>
   );
}

function affiliateDisclosure() {
   return (
      <Flex
         display="flex"
         gap={2}
         alignItems="start"
         fontSize="14px"
         fontWeight="400"
         lineHeight="14px"
         textTransform="none"
         background="gray.100"
         color="gray.700"
         border="1px solid"
         borderColor="gray.300"
         borderRadius="md"
         padding={4}
         marginTop={4}
      >
         <FaIcon size="1x" icon={faLink} color="gray.500" />
         {_js('iFixit earns commission when you buy through these links.')}
      </Flex>
   );
}
